import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Map,
  Plus,
  Search,
  Edit2,
  Trash2,
  RefreshCw,
  Check,
  AlertCircle,
  ChevronLeft,
  Save,
  X,
  Sparkles,
  Zap,
  Bug,
  Puzzle,
  Rocket,
  Target,
  Lightbulb,
  CheckCircle2,
  MessageSquarePlus,
  ChevronUp,
  ArrowRightCircle,
  Mail,
  Clock,
  AlertTriangle,
  Image as ImageIcon,
  ExternalLink,
  Eye,
} from 'lucide-react';
import { useAdminStore } from '../../store/admin';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

interface RoadmapItem {
  id: number;
  title: string;
  description: string | null;
  status: 'completed' | 'in_progress' | 'planned' | 'consideration';
  category: 'feature' | 'improvement' | 'bug_fix' | 'integration';
  votes_count: number;
  priority: number;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

interface FeatureRequest {
  id: number;
  title: string;
  description: string | null;
  category: string;
  submitted_by_ip: string;
  submitted_by_email: string | null;
  status: 'pending' | 'approved' | 'rejected';
  converted_to_roadmap_id: number | null;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
}

interface IssueReport {
  id: number;
  title: string;
  description: string;
  image_url: string | null;
  image_key: string | null;
  submitted_by_ip: string;
  submitted_by_email: string | null;
  status: 'pending' | 'investigating' | 'resolved' | 'closed';
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

type ViewMode = 'list' | 'create' | 'edit' | 'requests' | 'issues';

const issueStatusConfig = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-600' },
  investigating: { label: 'Investigating', color: 'bg-blue-100 text-blue-600' },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-600' },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-600' },
};

const statusConfig = {
  in_progress: {
    label: 'In Progress',
    icon: Rocket,
    color: 'bg-blue-100 text-blue-600',
  },
  planned: {
    label: 'Planned',
    icon: Target,
    color: 'bg-purple-100 text-purple-600',
  },
  consideration: {
    label: 'Under Consideration',
    icon: Lightbulb,
    color: 'bg-amber-100 text-amber-600',
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle2,
    color: 'bg-green-100 text-green-600',
  },
};

const categoryConfig = {
  feature: { label: 'Feature', icon: Sparkles, color: 'bg-purple-100 text-purple-600' },
  improvement: { label: 'Improvement', icon: Zap, color: 'bg-cyan-100 text-cyan-600' },
  bug_fix: { label: 'Bug Fix', icon: Bug, color: 'bg-red-100 text-red-600' },
  integration: { label: 'Integration', icon: Puzzle, color: 'bg-green-100 text-green-600' },
};

export default function RoadmapManagement() {
  const { adminToken } = useAdminStore();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get view mode from URL, default to 'list'
  const viewModeParam = searchParams.get('view') as ViewMode | null;
  const viewMode: ViewMode = viewModeParam && ['list', 'create', 'edit', 'requests', 'issues'].includes(viewModeParam)
    ? viewModeParam
    : 'list';

  // Helper function to update view mode in URL
  const setViewMode = useCallback((mode: ViewMode) => {
    const newParams = new URLSearchParams(searchParams);
    if (mode === 'list') {
      newParams.delete('view');
      newParams.delete('editId');
    } else {
      newParams.set('view', mode);
      if (mode !== 'edit') {
        newParams.delete('editId');
      }
    }
    setSearchParams(newParams, { replace: false });
  }, [searchParams, setSearchParams]);
  const [items, setItems] = useState<RoadmapItem[]>([]);
  const [requests, setRequests] = useState<FeatureRequest[]>([]);
  const [issueReports, setIssueReports] = useState<IssueReport[]>([]);
  const [selectedItem, setSelectedItem] = useState<RoadmapItem | null>(null);
  const [selectedIssue, setSelectedIssue] = useState<IssueReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [requestFilter, setRequestFilter] = useState<string>('pending');
  const [issueFilter, setIssueFilter] = useState<string>('pending');

  // Form state
  const [form, setForm] = useState({
    title: '',
    description: '',
    status: 'consideration' as RoadmapItem['status'],
    category: 'feature' as RoadmapItem['category'],
    priority: 0,
  });

  const fetchItems = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/roadmap`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const data: ApiResponse<RoadmapItem[]> = await response.json();
      if (data.success) {
        setItems(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch roadmap items:', err);
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  const fetchRequests = useCallback(async () => {
    if (!adminToken) return;
    try {
      const response = await fetch(
        `${API_URL}/admin/roadmap/feature-requests?status=${requestFilter}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      const data: ApiResponse<FeatureRequest[]> = await response.json();
      if (data.success) {
        setRequests(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch feature requests:', err);
    }
  }, [adminToken, requestFilter]);

  const fetchIssueReports = useCallback(async () => {
    if (!adminToken) return;
    try {
      const response = await fetch(
        `${API_URL}/admin/roadmap/issue-reports?status=${issueFilter}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      const data: ApiResponse<IssueReport[]> = await response.json();
      if (data.success) {
        setIssueReports(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch issue reports:', err);
    }
  }, [adminToken, issueFilter]);

  useEffect(() => {
    fetchItems();
    fetchRequests();
    fetchIssueReports();
  }, [fetchItems, fetchRequests, fetchIssueReports]);

  // Restore edit state from URL when navigating back
  useEffect(() => {
    const editId = searchParams.get('editId');
    if (viewMode === 'edit' && editId && items.length > 0) {
      const item = items.find(i => i.id === parseInt(editId, 10));
      if (item && (!selectedItem || selectedItem.id !== item.id)) {
        setSelectedItem(item);
        setForm({
          title: item.title,
          description: item.description || '',
          status: item.status,
          category: item.category,
          priority: item.priority,
        });
      }
    }
  }, [viewMode, searchParams, items, selectedItem]);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const handleCreate = () => {
    setSelectedItem(null);
    setForm({
      title: '',
      description: '',
      status: 'consideration',
      category: 'feature',
      priority: 0,
    });
    setViewMode('create');
  };

  const handleEdit = (item: RoadmapItem) => {
    setSelectedItem(item);
    setForm({
      title: item.title,
      description: item.description || '',
      status: item.status,
      category: item.category,
      priority: item.priority,
    });
    // Update URL with edit mode and item ID
    const newParams = new URLSearchParams(searchParams);
    newParams.set('view', 'edit');
    newParams.set('editId', item.id.toString());
    setSearchParams(newParams, { replace: false });
  };

  const handleSave = async () => {
    if (!form.title) {
      setError('Title is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const url = selectedItem
        ? `${API_URL}/admin/roadmap/${selectedItem.id}`
        : `${API_URL}/admin/roadmap`;
      const method = selectedItem ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify(form),
      });

      const data: ApiResponse<RoadmapItem> = await response.json();
      if (data.success) {
        setSuccess(selectedItem ? 'Item updated successfully!' : 'Item created successfully!');
        setViewMode('list');
        fetchItems();
      } else {
        setError(data.error || 'Failed to save item');
      }
    } catch {
      setError('Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this item?')) return;

    try {
      const response = await fetch(`${API_URL}/admin/roadmap/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${adminToken}` },
      });

      const data: ApiResponse<null> = await response.json();
      if (data.success) {
        setSuccess('Item deleted successfully');
        fetchItems();
      } else {
        setError(data.error || 'Failed to delete item');
      }
    } catch {
      setError('Failed to delete item');
    }
  };

  const handleUpdateRequestStatus = async (id: number, status: string) => {
    try {
      const response = await fetch(`${API_URL}/admin/roadmap/feature-requests/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ status }),
      });

      const data: ApiResponse<null> = await response.json();
      if (data.success) {
        setSuccess(`Request ${status}`);
        fetchRequests();
      } else {
        setError(data.error || 'Failed to update request');
      }
    } catch {
      setError('Failed to update request');
    }
  };

  const handleConvertRequest = async (id: number) => {
    try {
      const response = await fetch(`${API_URL}/admin/roadmap/feature-requests/${id}/convert`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken}` },
      });

      const data: ApiResponse<RoadmapItem> = await response.json();
      if (data.success) {
        setSuccess('Request converted to roadmap item!');
        fetchRequests();
        fetchItems();
      } else {
        setError(data.error || 'Failed to convert request');
      }
    } catch {
      setError('Failed to convert request');
    }
  };

  const handleDeleteRequest = async (id: number) => {
    if (!confirm('Are you sure you want to delete this request?')) return;

    try {
      const response = await fetch(`${API_URL}/admin/roadmap/feature-requests/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${adminToken}` },
      });

      const data: ApiResponse<null> = await response.json();
      if (data.success) {
        setSuccess('Request deleted');
        fetchRequests();
      } else {
        setError(data.error || 'Failed to delete request');
      }
    } catch {
      setError('Failed to delete request');
    }
  };

  const handleUpdateIssueStatus = async (id: number, status: string) => {
    try {
      const response = await fetch(`${API_URL}/admin/roadmap/issue-reports/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ status }),
      });

      const data: ApiResponse<null> = await response.json();
      if (data.success) {
        setSuccess(`Issue marked as ${status}`);
        fetchIssueReports();
        setSelectedIssue(null);
      } else {
        setError(data.error || 'Failed to update issue');
      }
    } catch {
      setError('Failed to update issue');
    }
  };

  const handleDeleteIssue = async (id: number) => {
    if (!confirm('Are you sure you want to delete this issue report?')) return;

    try {
      const response = await fetch(`${API_URL}/admin/roadmap/issue-reports/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${adminToken}` },
      });

      const data: ApiResponse<null> = await response.json();
      if (data.success) {
        setSuccess('Issue report deleted');
        fetchIssueReports();
        setSelectedIssue(null);
      } else {
        setError(data.error || 'Failed to delete issue');
      }
    } catch {
      setError('Failed to delete issue');
    }
  };

  const filteredItems = items.filter((item) => {
    const matchesSearch =
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const pendingRequestsCount = requests.filter((r) => r.status === 'pending').length;
  const pendingIssuesCount = issueReports.filter((r) => r.status === 'pending').length;

  if (loading && viewMode === 'list') {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
      className="space-y-6"
    >
      {/* Messages */}
      <AnimatePresence>
        {(error || success) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`p-4 rounded-xl flex items-center gap-3 ${
              error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'
            }`}
          >
            {error ? (
              <AlertCircle className="w-5 h-5 text-red-500" />
            ) : (
              <Check className="w-5 h-5 text-green-500" />
            )}
            <span className={error ? 'text-red-600' : 'text-green-600'}>{error || success}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* List View */}
      {viewMode === 'list' && (
        <>
          {/* Header */}
          <motion.div variants={fadeInUp} className="flex flex-col gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1 sm:mb-2">Roadmap Management</h1>
              <p className="text-sm sm:text-base text-slate-500">Manage your product roadmap and feature requests</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={() => setViewMode('issues')}
                className="relative px-4 sm:px-6 py-2.5 rounded-xl border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 transition-colors flex items-center justify-center"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Issue Reports
                {pendingIssuesCount > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                    {pendingIssuesCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setViewMode('requests')}
                className="relative px-4 sm:px-6 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors flex items-center justify-center"
              >
                <MessageSquarePlus className="w-4 h-4 mr-2" />
                Feature Requests
                {pendingRequestsCount > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                    {pendingRequestsCount}
                  </span>
                )}
              </button>
              <button
                onClick={handleCreate}
                className="px-4 sm:px-6 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-medium shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow flex items-center justify-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </button>
            </div>
          </motion.div>

          {/* Filters */}
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search items..."
                className="w-full h-11 pl-12 pr-4 bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {['all', 'in_progress', 'planned', 'consideration', 'completed'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    filterStatus === status
                      ? 'bg-purple-500 text-white'
                      : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {status === 'all'
                    ? 'All'
                    : status === 'in_progress'
                    ? 'In Progress'
                    : status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Items Grid */}
          <motion.div variants={fadeInUp} className="grid gap-4">
            {filteredItems.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center shadow-sm">
                <Map className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Roadmap Items</h3>
                <p className="text-slate-500 mb-4">Add your first roadmap item to get started</p>
                <button
                  onClick={handleCreate}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-medium shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow inline-flex items-center"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item
                </button>
              </div>
            ) : (
              filteredItems.map((item) => {
                const StatusIcon = statusConfig[item.status].icon;
                const CategoryIcon = categoryConfig[item.category].icon;

                return (
                  <div
                    key={item.id}
                    className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-purple-300 transition-colors shadow-sm"
                  >
                    <div className="flex items-start gap-4">
                      {/* Vote Count */}
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 rounded-xl bg-slate-100 flex flex-col items-center justify-center text-slate-600">
                          <ChevronUp className="w-4 h-4" />
                          <span className="text-xs font-bold">{item.votes_count}</span>
                        </div>
                        <span className="text-xs text-slate-400 mt-1">votes</span>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <h3 className="text-lg font-semibold text-slate-900">{item.title}</h3>
                          <div className="flex gap-2 flex-shrink-0">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                                statusConfig[item.status].color
                              }`}
                            >
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig[item.status].label}
                            </span>
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                                categoryConfig[item.category].color
                              }`}
                            >
                              <CategoryIcon className="w-3 h-3" />
                              {categoryConfig[item.category].label}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-slate-500 line-clamp-2 mb-2">
                          {item.description || 'No description'}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-slate-400">
                          <span>Priority: {item.priority}</span>
                          <span>Created: {new Date(item.created_at).toLocaleDateString()}</span>
                          {item.completed_at && (
                            <span>
                              Completed: {new Date(item.completed_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 rounded-lg bg-slate-100 hover:bg-red-100 text-slate-500 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </motion.div>
        </>
      )}

      {/* Create/Edit View */}
      {(viewMode === 'create' || viewMode === 'edit') && (
        <>
          {/* Header */}
          <motion.div variants={fadeInUp} className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setViewMode('list')}
                className="p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-500 hover:text-slate-700 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">
                  {viewMode === 'create' ? 'Add Roadmap Item' : 'Edit Roadmap Item'}
                </h1>
                <p className="text-slate-500">
                  {viewMode === 'create'
                    ? 'Add a new item to your roadmap'
                    : `Editing: ${selectedItem?.title}`}
                </p>
              </div>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-medium shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow flex items-center"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save
            </button>
          </motion.div>

          {/* Form */}
          <motion.div variants={fadeInUp} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">Title *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Enter title..."
                className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Enter description..."
                rows={4}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900 resize-none"
              />
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value as RoadmapItem['status'] })}
                  className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
                >
                  <option value="consideration">Under Consideration</option>
                  <option value="planned">Planned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">Category</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value as RoadmapItem['category'] })}
                  className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
                >
                  <option value="feature">Feature</option>
                  <option value="improvement">Improvement</option>
                  <option value="bug_fix">Bug Fix</option>
                  <option value="integration">Integration</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">Priority</label>
                <input
                  type="number"
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })}
                  min={0}
                  max={100}
                  className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
                />
              </div>
            </div>
          </motion.div>
        </>
      )}

      {/* Feature Requests View */}
      {viewMode === 'requests' && (
        <>
          {/* Header */}
          <motion.div variants={fadeInUp} className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setViewMode('list')}
                className="p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-500 hover:text-slate-700 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Feature Requests</h1>
                <p className="text-slate-500">Review and manage user-submitted feature requests</p>
              </div>
            </div>
          </motion.div>

          {/* Request Filters */}
          <motion.div variants={fadeInUp} className="flex gap-2">
            {['pending', 'approved', 'rejected', 'all'].map((status) => (
              <button
                key={status}
                onClick={() => setRequestFilter(status)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  requestFilter === status
                    ? 'bg-purple-500 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </motion.div>

          {/* Requests List */}
          <motion.div variants={fadeInUp} className="space-y-4">
            {requests.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center shadow-sm">
                <MessageSquarePlus className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Feature Requests</h3>
                <p className="text-slate-500">No {requestFilter} requests at the moment</p>
              </div>
            ) : (
              requests.map((request) => (
                <div
                  key={request.id}
                  className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <h3 className="text-lg font-semibold text-slate-900">{request.title}</h3>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            request.status === 'pending'
                              ? 'bg-amber-100 text-amber-600'
                              : request.status === 'approved'
                              ? 'bg-green-100 text-green-600'
                              : 'bg-red-100 text-red-600'
                          }`}
                        >
                          {request.status}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 mb-3">
                        {request.description || 'No description provided'}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(request.created_at).toLocaleDateString()}
                        </span>
                        {request.submitted_by_email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {request.submitted_by_email}
                          </span>
                        )}
                        <span
                          className={`px-2 py-0.5 rounded-full ${
                            categoryConfig[request.category as keyof typeof categoryConfig]?.color ||
                            'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {request.category}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    {request.status === 'pending' && (
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleConvertRequest(request.id)}
                          className="p-2 rounded-lg bg-purple-100 hover:bg-purple-200 text-purple-600 transition-colors"
                          title="Add to Roadmap"
                        >
                          <ArrowRightCircle className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleUpdateRequestStatus(request.id, 'approved')}
                          className="p-2 rounded-lg bg-green-100 hover:bg-green-200 text-green-600 transition-colors"
                          title="Approve"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleUpdateRequestStatus(request.id, 'rejected')}
                          className="p-2 rounded-lg bg-red-100 hover:bg-red-200 text-red-600 transition-colors"
                          title="Reject"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    {request.status !== 'pending' && (
                      <button
                        onClick={() => handleDeleteRequest(request.id)}
                        className="p-2 rounded-lg bg-slate-100 hover:bg-red-100 text-slate-500 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </motion.div>
        </>
      )}

      {/* Issue Reports View */}
      {viewMode === 'issues' && (
        <>
          {/* Header */}
          <motion.div variants={fadeInUp} className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setViewMode('list')}
                className="p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-500 hover:text-slate-700 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Issue Reports</h1>
                <p className="text-slate-500">Review and manage user-submitted issue reports</p>
              </div>
            </div>
          </motion.div>

          {/* Issue Filters */}
          <motion.div variants={fadeInUp} className="flex gap-2">
            {['pending', 'investigating', 'resolved', 'closed', 'all'].map((status) => (
              <button
                key={status}
                onClick={() => setIssueFilter(status)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  issueFilter === status
                    ? 'bg-red-500 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </motion.div>

          {/* Issues List */}
          <motion.div variants={fadeInUp} className="space-y-4">
            {issueReports.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center shadow-sm">
                <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Issue Reports</h3>
                <p className="text-slate-500">No {issueFilter} issue reports at the moment</p>
              </div>
            ) : (
              issueReports.map((issue) => (
                <div
                  key={issue.id}
                  className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm"
                >
                  <div className="flex items-start gap-4">
                    {/* Image Preview */}
                    {issue.image_url && (
                      <div className="flex-shrink-0">
                        <button
                          onClick={() => setSelectedIssue(issue)}
                          className="w-20 h-20 rounded-xl overflow-hidden border border-slate-200 hover:border-purple-300 transition-colors relative group"
                        >
                          <img
                            src={issue.image_url}
                            alt="Issue screenshot"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Eye className="w-5 h-5 text-white" />
                          </div>
                        </button>
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <h3 className="text-lg font-semibold text-slate-900">{issue.title}</h3>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            issueStatusConfig[issue.status].color
                          }`}
                        >
                          {issueStatusConfig[issue.status].label}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 mb-3 line-clamp-2">
                        {issue.description}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(issue.created_at).toLocaleDateString()}
                        </span>
                        {issue.submitted_by_email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {issue.submitted_by_email}
                          </span>
                        )}
                        {issue.image_url && (
                          <span className="flex items-center gap-1 text-purple-500">
                            <ImageIcon className="w-3 h-3" />
                            Has screenshot
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => setSelectedIssue(issue)}
                        className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {issue.status === 'pending' && (
                        <button
                          onClick={() => handleUpdateIssueStatus(issue.id, 'investigating')}
                          className="p-2 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-600 transition-colors"
                          title="Mark as Investigating"
                        >
                          <Search className="w-4 h-4" />
                        </button>
                      )}
                      {(issue.status === 'pending' || issue.status === 'investigating') && (
                        <button
                          onClick={() => handleUpdateIssueStatus(issue.id, 'resolved')}
                          className="p-2 rounded-lg bg-green-100 hover:bg-green-200 text-green-600 transition-colors"
                          title="Mark as Resolved"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteIssue(issue.id)}
                        className="p-2 rounded-lg bg-slate-100 hover:bg-red-100 text-slate-500 hover:text-red-500 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </motion.div>
        </>
      )}

      {/* Issue Detail Modal */}
      <AnimatePresence>
        {selectedIssue && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedIssue(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-2xl bg-white rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Issue Report Details</h3>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        issueStatusConfig[selectedIssue.status].color
                      }`}
                    >
                      {issueStatusConfig[selectedIssue.status].label}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedIssue(null)}
                  className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-slate-500 mb-1">Title</h4>
                  <p className="text-slate-900 font-medium">{selectedIssue.title}</p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-slate-500 mb-1">Description</h4>
                  <p className="text-slate-700 whitespace-pre-wrap">{selectedIssue.description}</p>
                </div>

                {selectedIssue.image_url && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-500 mb-2">Screenshot</h4>
                    <div className="relative">
                      <img
                        src={selectedIssue.image_url}
                        alt="Issue screenshot"
                        className="w-full rounded-xl border border-slate-200"
                      />
                      <a
                        href={selectedIssue.image_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute top-2 right-2 p-2 rounded-lg bg-white/90 hover:bg-white text-slate-600 transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-200">
                  <div>
                    <h4 className="text-sm font-medium text-slate-500 mb-1">Submitted</h4>
                    <p className="text-slate-700">
                      {new Date(selectedIssue.created_at).toLocaleString()}
                    </p>
                  </div>
                  {selectedIssue.submitted_by_email && (
                    <div>
                      <h4 className="text-sm font-medium text-slate-500 mb-1">Email</h4>
                      <p className="text-slate-700">{selectedIssue.submitted_by_email}</p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-slate-200">
                  {selectedIssue.status === 'pending' && (
                    <button
                      onClick={() => handleUpdateIssueStatus(selectedIssue.id, 'investigating')}
                      className="flex-1 h-11 rounded-xl bg-blue-500 text-white font-medium hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                    >
                      <Search className="w-4 h-4" />
                      Investigate
                    </button>
                  )}
                  {(selectedIssue.status === 'pending' || selectedIssue.status === 'investigating') && (
                    <button
                      onClick={() => handleUpdateIssueStatus(selectedIssue.id, 'resolved')}
                      className="flex-1 h-11 rounded-xl bg-green-500 text-white font-medium hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                    >
                      <Check className="w-4 h-4" />
                      Mark Resolved
                    </button>
                  )}
                  {selectedIssue.status !== 'closed' && (
                    <button
                      onClick={() => handleUpdateIssueStatus(selectedIssue.id, 'closed')}
                      className="flex-1 h-11 rounded-xl bg-slate-500 text-white font-medium hover:bg-slate-600 transition-colors flex items-center justify-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Close
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteIssue(selectedIssue.id)}
                    className="h-11 px-4 rounded-xl bg-red-100 text-red-600 font-medium hover:bg-red-200 transition-colors flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
